/**
 * livery/types.ts (M144) — the shared shapes of the THREE-VIEW livery system.
 *
 * A livery is no longer one side-view painting stretched over the whole car.
 * It is designed the way a real livery is, in orthographic views:
 *
 *   SIDE   length × height   both flanks (the nose is at x = 0)
 *   TOP    length × width    hood, roof, deck, bed: anything facing the sky
 *   FRONT  width × height    the nose face
 *   REAR   width × height    the tail face (what the chase camera looks at)
 *
 * The paint shader picks the view per pixel from the surface normal, so a
 * striped shirt can run its stripes down the hood AND stand them upright on
 * the doors, a collar can sit on the hood under the windscreen, and a dragon
 * can have a skull on the roof and a face on the flank.
 *
 * Painters draw in METRES: every view's context is pre-scaled so one unit is
 * one world unit on the car. A circle is a circle on every vehicle, and a
 * 0.2 m stripe is the same stripe on the hood and on the door.
 */

/** One measured vehicle body, in world units (about metres). */
export interface BodyFit {
  /** Painted length (nose → tail), height (sill → roof) and width. */
  L: number;
  H: number;
  W: number;
  /** Where the glass starts, as a fraction of H from the lowest painted point. */
  belt: number;
  /** The greenhouse along the length: metres from the nose to the base of the
   *  windscreen (x0) and to the end of the rear glass (x1). Absent when the
   *  ride has no cabin glass (the Formula) or only a fly screen (the bike). */
  cabin?: { x0: number; x1: number };
  /** Wheel hubs in SIDE-view metres: x from the nose, y DOWN from the roof
   *  line, r = tyre radius. Lets a painter put a cuff round a wheel arch. */
  wheels: { x: number; y: number; r: number }[];
  /** M153: a SIDE SCOOP cut into the flank (the sports car's), as metres
   *  from the nose: the shirt billboard ends before it, a crest stays clear. */
  scoop?: { x0: number; x1: number };
  /** M154 polish r4: the side mirrors in SIDE-view metres (x from the nose,
   *  y down from the roof line), each grown by how far it stands out from
   *  the door: the paint it hides from the three-quarter views (a crest
   *  there sat behind the pickup's mirror from the angled view). */
  mirrors?: { x0: number; x1: number; y0: number; y1: number }[];
  /** M154 polish r5: the PANEL GAPS on the flank (a door's front and back
   *  edge): thin upright strips of non-paint at the flank, in SIDE-view
   *  metres (x from the nose; y0..y1 down from the roof line). Too thin for
   *  the flank map's cells: a canton's stars keep off them. */
  seams?: { x: number; y0: number; y1: number }[];
  /** M153: the FLANK MAP, a coarse grid over the side view (cols along the
   *  length from the nose, rows down from the roof line) marking cells where
   *  the body has OUTWARD-FACING PAINT. Scoops, windows, the bike's engine
   *  window, wheel arches and cut cavities are the gaps. Absent for the
   *  generic body. Read it through `flankCover`. */
  flank?: FlankMap;
  /** M153: the flank map with what stands IN FRONT of the paint taken out
   *  (the bike's rider, tyres, door handles, steps, mirrors): where a flag's
   *  crest can actually be SEEN from the side. Only the flag crest search
   *  reads it (the kits and the dragon read `flank`). */
  flankClear?: FlankMap;
  /** M153: the PLAN MAP, the same grid seen from above (cols along the length
   *  from the nose, rows across the width from one side): cells where the
   *  body has UPWARD-FACING paint. The coupe's hood dishes, the monster
   *  truck's hood vent, the pickup's bed, every glass panel are the gaps.
   *  Read it through `planCover`. */
  plan?: FlankMap;
  /** M154 polish: the FOLD MAP, on the flank map's grid: cells where a paint
   *  surface facing mostly UP (and the top of the body there) lies — a
   *  shoulder, a sidepod top, a low nose. The side-view flag painting lands
   *  on it stretched along the car, so a crest there folds over the
   *  shoulder (the Formula's Japan disc, Brazil diamond, USA stars). Read it
   *  through `foldCover`. */
  fold?: FlankMap;
  /** M154 polish r2: the SMEAR MAP, on the same grid: cells where the top
   *  of the body is a broad surface that does not face sideways (a hood, a
   *  steep hood plane, a nose, a deck), at any height. The side painting is
   *  drawn ACROSS it, so a small emblem there (a canton's stars) tears into
   *  slashes over the hood. Read it through `smearCover`. */
  smear?: FlankMap;
  /** M153: where the CABIN's glass ends (metres from the nose): the side
   *  windows plus a rear window that is part of the greenhouse (the coupe's
   *  fastback window), but not low glazing over an engine (the sports car).
   *  The shirt's back panel starts here. */
  glassEnd?: number;
  /** M153: an open COCKPIT with no glass round it (the Formula): from just
   *  ahead of the driver's helmet to just behind it. The kits dress it like
   *  a cabin (the collar rings it, the hood is the nose ahead of it). */
  cockpit?: { x0: number; x1: number };
  /** The ride being painted ('' when unknown: the generic fit). */
  vehicle: string;
}

export interface FlankMap {
  cols: number;
  rows: number;
  /** rows * cols, row-major, 1 = side-facing paint. */
  cov: Uint8Array;
  /** M154 polish (side maps only): how far out from the centre line the
   *  outermost paint in each cell lies (-Infinity = none): tells a sidepod's
   *  wall from the engine cover's flank 30 cm further in, which line up
   *  from the side but part from any other angle. */
  depth?: Float32Array;
  /** M154 polish r4 (the plan map only): the topmost up-facing paint's height
   *  in each cell, metres DOWN from the roof line as the side view counts
   *  (-1 = none), and its normal's up component. */
  height?: Float32Array;
  ny?: Float32Array;
}

/**
 * M153: the share of a side-view rectangle (metres: x from the nose, y down
 * from the roof line) that is outward-facing paint. 1 when the fit carries no
 * flank map (the generic body: every placement is allowed).
 */
export function flankCover(fit: BodyFit, x0: number, x1: number, y0: number, y1: number): number {
  return mapCover(fit.flank, fit.L, fit.H, x0, x1, y0, y1);
}

/**
 * M154 polish: the share of a side-view rectangle's footprint (the ellipse
 * inscribed in it: metres, as flankCover) that lies over an up-facing deck
 * (the fold map). 0 when the fit carries no fold map.
 */
export function foldCover(fit: BodyFit, x0: number, x1: number, y0: number, y1: number): number {
  if (!fit.fold) return 0;
  const f = fit.fold;
  let n = 0;
  let on = 0;
  roundCells(f, fit.L, fit.H, x0, x1, y0, y1, (k) => {
    n++;
    on += f.cov[k]!;
  });
  return n ? on / n : 0;
}

/**
 * M154 polish r2: the share of a side-view rectangle's footprint (the ellipse
 * inscribed in it, metres) that lies over a surface the side painting is
 * smeared across (the smear map). 0 when the fit carries none.
 */
export function smearCover(fit: BodyFit, x0: number, x1: number, y0: number, y1: number): number {
  if (!fit.smear) return 0;
  const f = fit.smear;
  let n = 0;
  let on = 0;
  roundCells(f, fit.L, fit.H, x0, x1, y0, y1, (k) => {
    n++;
    on += f.cov[k]!;
  });
  return n ? on / n : 0;
}

/**
 * M154 polish: visit the map cells of a side-view rectangle (metres) whose
 * centres lie inside the ELLIPSE inscribed in it: a crest's own footprint
 * (a disc, a star, a diamond), not its box's empty corners. A rectangle too
 * small to hold a cell centre visits its box.
 */
function roundCells(f: FlankMap, L: number, H: number, x0: number, x1: number, y0: number, y1: number, visit: (k: number) => void): void {
  const xa = Math.min(x0, x1);
  const xb = Math.max(x0, x1);
  const ya = Math.min(y0, y1);
  const yb = Math.max(y0, y1);
  const c0 = Math.max(0, Math.floor((xa / L) * f.cols));
  const c1 = Math.min(f.cols - 1, Math.floor((xb / L) * f.cols));
  const r0 = Math.max(0, Math.floor((ya / H) * f.rows));
  const r1 = Math.min(f.rows - 1, Math.floor((yb / H) * f.rows));
  if (c1 < c0 || r1 < r0) return;
  const cx = (xa + xb) / 2;
  const cy = (ya + yb) / 2;
  const rx = Math.max(1e-9, (xb - xa) / 2);
  const ry = Math.max(1e-9, (yb - ya) / 2);
  const inside: number[] = [];
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      const px = ((c + 0.5) / f.cols) * L;
      const py = ((r + 0.5) / f.rows) * H;
      if (((px - cx) / rx) ** 2 + ((py - cy) / ry) ** 2 <= 1) inside.push(r * f.cols + c);
    }
  }
  if (inside.length) for (const k of inside) visit(k);
  else for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) visit(r * f.cols + c);
}

/** M154 polish: a step this big in depth between a crest's cells puts it on
 *  two surfaces (a sidepod's wall and the engine cover's flank behind it). */
export const LAYER_STEP = 0.18;

/**
 * M154 polish: the share of a side-view rectangle's footprint (the ellipse
 * inscribed in it: metres, as flankCover) whose seen paint lies OFF its main
 * surface: further than
 * LAYER_STEP in or out from the median depth of its paint cells (a crest
 * across the Formula's sidepod wall and its engine cover's flank reads whole
 * from the side and as two halves from every other view). 0 when the map
 * carries no depth (the generic body).
 */
export function offLayerShare(f: FlankMap | undefined, L: number, H: number, x0: number, x1: number, y0: number, y1: number): number {
  if (!f || !f.depth) return 0;
  const depth = f.depth;
  const ds: number[] = [];
  let n = 0;
  roundCells(f, L, H, x0, x1, y0, y1, (k) => {
    n++;
    const d = depth[k]!;
    if (f.cov[k] && Number.isFinite(d)) ds.push(d);
  });
  if (!ds.length) return 0;
  const sorted = [...ds].sort((a, b) => a - b);
  const med = sorted[Math.floor(sorted.length / 2)]!;
  let off = 0;
  for (const d of ds) if (Math.abs(d - med) > LAYER_STEP) off++;
  return off / n;
}

/**
 * M153: the share of a top-view rectangle (metres: x from the nose, z across
 * from the body's first side) that is upward-facing paint. 1 when the fit
 * carries no plan map.
 */
export function planCover(fit: BodyFit, x0: number, x1: number, z0: number, z1: number): number {
  return mapCover(fit.plan, fit.L, fit.W, x0, x1, z0, z1);
}

function mapCover(f: FlankMap | undefined, L: number, H: number, x0: number, x1: number, y0: number, y1: number): number {
  const fit = { L, H };
  if (!f) return 1;
  const c0 = Math.max(0, Math.floor((Math.min(x0, x1) / fit.L) * f.cols));
  const c1 = Math.min(f.cols - 1, Math.floor((Math.max(x0, x1) / fit.L) * f.cols));
  const r0 = Math.max(0, Math.floor((Math.min(y0, y1) / fit.H) * f.rows));
  const r1 = Math.min(f.rows - 1, Math.floor((Math.max(y0, y1) / fit.H) * f.rows));
  if (c1 < c0 || r1 < r0) return 0;
  let n = 0;
  let on = 0;
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      n++;
      on += f.cov[r * f.cols + c];
    }
  }
  return n ? on / n : 0;
}

/** The generic body the placeholder car and the bulldozer form are painted for. */
export const DEFAULT_BODY: BodyFit = {
  L: 4,
  H: 1,
  W: 1.7,
  belt: 0.62,
  cabin: { x0: 1.35, x1: 3.05 },
  wheels: [
    { x: 0.75, y: 0.9, r: 0.34 },
    { x: 3.2, y: 0.9, r: 0.34 },
  ],
  vehicle: '',
};

/** The four drawing surfaces a painter fills, each scaled to metres. */
export interface LiveryViews {
  /** x: 0 nose → L tail. y: 0 roof → H sill. */
  side: CanvasRenderingContext2D;
  /** x: 0 nose → L tail. y: 0 left edge → W right edge (centreline W / 2). */
  top: CanvasRenderingContext2D;
  /** x: 0 → W across the car. y: 0 top → H bottom. */
  front: CanvasRenderingContext2D;
  rear: CanvasRenderingContext2D;
  fit: BodyFit;
}

/** A painter fills the views; `data` (when asked for) is the matching set of
 *  views for the relief / glow / gloss map (R height, G glow, B roughness). */
export type LiveryPainter = (v: LiveryViews, data?: LiveryViews) => void;

export const css = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

/** #rrggbb number → rgba() string. */
export function rgba(n: number, a: number): string {
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/** Mix two colours (t = 0 → a, 1 → b). */
export function mixColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

/** Perceived lightness 0..1 (for choosing a readable ink over a colour). */
export function luma(n: number): number {
  return (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
}
